import dynamic from 'next/dynamic';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import { RightContext } from '@/components/RightProvider/context';

import styles from '../styles/index.module.scss';
import { ModelPageConfig } from '../types';

const HeroOperation = dynamic(() => import('./HeroOperation'), { ssr: false });
const ImageOperation = dynamic(
  () => import('@/3.0/image-generator/_components/operation'),
  { ssr: false },
);

interface Props {
  config: ModelPageConfig;
}

const ModelLandingPage: React.FC<Props> = ({ config }) => {
  const rightContext = useContext(RightContext);
  const router = useRouter();

  const handleLogin = useCallback(async () => {
    await rightContext.getUserLoginState({
      onReady: () => {},
    });
  }, [rightContext]);

  /** 拦截左侧"开始"按钮点击，跳转到对应生成页 */
  const handleHeroLeftClick = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const btn = target.closest('button.ant-btn-primary');
      if (btn) {
        e.preventDefault();
        e.stopPropagation();
        router.push(
          config.heroOperationType === 'image'
            ? '/ai-image-generator'
            : '/ai-video-generator',
        );
      }
    },
    [router, config.heroOperationType],
  );

  return (
    <div className={styles.page}>
      {/* ── Hero ── */}
      <div className={styles.heroWrapper}>
        <section className={styles.hero}>
          <div className={styles.heroHeader}>
            <h1 className={styles.heroTitle}>{config.hero.title}</h1>
            <p className={styles.heroDesc}>{config.hero.description}</p>
          </div>
          <div
            className={
              config.heroOperationType === 'image'
                ? styles.heroContentImage
                : styles.heroContent
            }
          >
            <div className={styles.heroLeft} onClickCapture={handleHeroLeftClick}>
              {config.heroOperationType === 'image' ? (
                <ImageOperation />
              ) : (
                <HeroOperation />
              )}
            </div>
            <div
              className={
                config.heroOperationType === 'image'
                  ? styles.heroRightImage
                  : styles.heroRight
              }
            >
              {config.hero.videos && config.hero.videos.length > 0 && (
                <HeroVideoCarousel videos={config.hero.videos} />
              )}
              {config.hero.images && config.hero.images.length > 0 && (
                <HeroImageCarousel images={config.hero.images} />
              )}
            </div>
          </div>
        </section>
      </div>

      {/* ── Features (Why Creators Choose) ── */}
      <div className={styles.featuresWrapper}>
        <section className={styles.features}>
          <h2 className={styles.sectionTitle}>{config.features.sectionTitle}</h2>
          <div className={styles.featureGrid}>
            {config.features.items.map((item) => (
              <div key={item.title} className={styles.featureCard}>
                <h3 className={styles.featureCardTitle}>{item.title}</h3>
                <p className={styles.featureCardDesc}>{item.description}</p>
              </div>
            ))}
          </div>
          <div className={styles.featuresCenter}>
            <Link
              href={
                config.heroOperationType === 'image'
                  ? '/ai-image-generator'
                  : '/ai-video-generator'
              }
              className={styles.btnPrimary}
            >
              {config.heroOperationType === 'image'
                ? 'Create Images Now'
                : 'Create Videos Now'}
            </Link>
          </div>
        </section>
      </div>

      {/* ── Video Gallery ── */}
      {config.videoGallery && config.videoGallery.items.length > 0 && (
        <div className={styles.sectionAlt}>
          <VideoGallery gallery={config.videoGallery} />
        </div>
      )}

      {/* ── Showcase ── */}
      {config.showcase && (
        <section className={styles.showcase}>
          <h2 className={styles.sectionTitle}>{config.showcase.title}</h2>
          <p className={styles.sectionDesc}>{config.showcase.description}</p>
          <div className={styles.showcaseVideo}>
            {config.showcase.youtubeId ? (
              <iframe
                src={`https://www.youtube.com/embed/${config.showcase.youtubeId}?rel=0`}
                title={config.showcase.title}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : config.showcase.videoUrl ? (
              <video
                src={config.showcase.videoUrl}
                poster={config.showcase.posterUrl}
                controls
              />
            ) : (
              <div className={styles.showcasePlayBtn}>
                <svg viewBox="0 0 24 24" fill="white">
                  <path d="M8 5v14l11-7z" />
                </svg>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ── HowTo ── */}
      {config.howTo && (
        <section className={styles.howTo}>
          <h2 className={styles.sectionTitle}>{config.howTo.title}</h2>
          <div className={styles.howToGrid}>
            {config.howTo.steps.map((step) => (
              <div key={step.title} className={styles.howToCard}>
                <div className={styles.howToCardImage}>
                  {step.imageUrl && <img src={step.imageUrl} alt={step.title} />}
                </div>
                <div className={styles.howToCardBody}>
                  <h3 className={styles.howToCardTitle}>{step.title}</h3>
                  <p className={styles.howToCardDesc}>{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── WhyChoose ── */}
      {config.whyChoose && (
        <div className={styles.sectionAlt}>
          <section className={styles.whyChoose}>
            <h2 className={styles.sectionTitle}>{config.whyChoose.title}</h2>
            {config.whyChoose.description && (
              <p className={styles.sectionDesc}>{config.whyChoose.description}</p>
            )}
            <div className={styles.whyChooseList}>
              {config.whyChoose.items.map((item) => (
                <div key={item.tag} className={styles.whyChooseCard}>
                  <div className={styles.whyChooseCardContent}>
                    <div className={styles.whyChooseTextGroup}>
                      <div className={styles.whyChooseTag}>{item.tag}</div>
                      <h3 className={styles.whyChooseHeading}>{item.heading}</h3>
                      <p className={styles.whyChooseDesc}>{item.description}</p>
                    </div>
                    <ul className={styles.whyChooseBullets}>
                      {item.bullets.map((b) => (
                        <li key={b} className={styles.whyChooseBullet}>
                          <span className={styles.bulletIcon}>
                            <CheckIcon />
                          </span>
                          {b}
                        </li>
                      ))}
                    </ul>
                    <div className={styles.whyChooseButtons}>
                      <button
                        type="button"
                        onClick={handleLogin}
                        className={styles.btnGoogle}
                      >
                        <GoogleIcon /> Start Free With Google
                      </button>
                      <Link
                        href={
                          config.heroOperationType === 'image'
                            ? '/ai-image-generator'
                            : '/ai-video-generator'
                        }
                        className={styles.btnPrimary}
                      >
                        Try {config.modelName} Now
                      </Link>
                    </div>
                  </div>
                  <div className={styles.whyChooseImage}>
                    {item.imageUrl && <img src={item.imageUrl} alt={item.heading} />}
                  </div>
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {/* ── WhoCanUse ── */}
      {config.whoCanUse && (
        <section className={styles.whoCanUse}>
          <h2 className={styles.sectionTitle}>{config.whoCanUse.title}</h2>
          <div className={styles.whoCanUseGrid}>
            {config.whoCanUse.items.map((item) => (
              <div key={item.title} className={styles.whoCanUseCard}>
                <div className={styles.whoCanUseCardImage}>
                  {item.imageUrl && <img src={item.imageUrl} alt={item.title} />}
                </div>
                <div className={styles.whoCanUseCardBody}>
                  <h3 className={styles.whoCanUseCardTitle}>{item.title}</h3>
                  <p className={styles.whoCanUseCardDesc}>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Social Proof ── */}
      {config.socialProof && (
        <div className={styles.sectionDark}>
          <section className={styles.socialProof}>
            <div className={styles.socialProofInner}>
              {/* YouTube */}
              <div className={styles.socialGroup}>
                <h3 className={styles.socialGroupTitle}>
                  {config.socialProof.youtube.title}
                </h3>
                <YouTubeEmbedGrid
                  items={config.socialProof.youtube.items}
                  fallbacks={config.socialProof.youtube.fallbacks}
                />
              </div>

              {/* Reddit */}
              <div className={styles.socialGroup}>
                <h3 className={styles.socialGroupTitle}>
                  {config.socialProof.reddit.title}
                </h3>
                <RedditEmbedGrid
                  items={config.socialProof.reddit.items}
                  fallbacks={config.socialProof.reddit.fallbacks}
                />
              </div>

              {/* X / Twitter */}
              <div className={styles.socialGroup}>
                <h3 className={styles.socialGroupTitle}>
                  {config.socialProof.twitter.title}
                </h3>
                <TwitterEmbedGrid
                  items={config.socialProof.twitter.items}
                  fallbacks={config.socialProof.twitter.fallbacks}
                />
              </div>
            </div>
          </section>
        </div>
      )}

      {/* ── Explore Models （暂时隐藏）── */}
      {/* <section className={styles.exploreModels}>
        <h2 className={styles.sectionTitle}>{config.exploreModels.title}</h2>
        <div className={styles.modelTags}>
          {config.exploreModels.models.map((m) => (
            <Link key={m.name} href={m.href} className={styles.modelTag}>
              {m.name}
            </Link>
          ))}
        </div>
      </section> */}

      {/* ── Toolkits ── */}
      <div className={styles.sectionAlt}>
        <section className={styles.toolkits}>
          <h2 className={styles.sectionTitle}>{config.toolkits.title}</h2>
          <p className={styles.sectionDesc}>{config.toolkits.description}</p>
        </section>
        <div className={styles.toolkitMarquee}>
          <div className={styles.toolkitTrack}>
            {[...config.toolkits.items, ...config.toolkits.items].map((item, i) => (
              <Link
                key={`${item.title}-${i}`}
                href={item.href || '#'}
                className={styles.toolkitCard}
              >
                <div className={styles.toolkitCardImage}>
                  {item.imageUrl && <img src={item.imageUrl} alt={item.title} />}
                </div>
                <div className={styles.toolkitCardTitle}>{item.title}</div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* ── FAQ ── */}
      <section className={styles.faq}>
        <h2 className={styles.sectionTitle}>{config.faq.title}</h2>
        <div className={styles.faqList}>
          {config.faq.items.map((item) => (
            <FAQItem key={item.question} question={item.question} answer={item.answer} />
          ))}
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <CtaBanner config={config} />
    </div>
  );
};

/* ── Sub-components ── */

const ChevronIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
  >
    <path
      d="M9.5 6L15.5 12L9.5 18"
      stroke="#333333"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MuteIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#333"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" />
    <line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);

const VolumeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#333"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);

const HeroVideoCarousel: React.FC<{ videos: string[] }> = ({ videos }) => {
  const [active, setActive] = useState(0);
  const [muted, setMuted] = useState(true);
  const videoRef = useRef<HTMLVideoElement>(null);

  const handleEnded = useCallback(() => {
    setActive((prev) => (prev + 1) % videos.length);
  }, [videos.length]);

  const goPrev = useCallback(() => {
    setActive((prev) => (prev - 1 + videos.length) % videos.length);
  }, [videos.length]);

  const goNext = useCallback(() => {
    setActive((prev) => (prev + 1) % videos.length);
  }, [videos.length]);

  const toggleMute = useCallback(() => {
    setMuted((prev) => {
      if (videoRef.current) videoRef.current.muted = !prev;
      return !prev;
    });
  }, []);

  return (
    <div className={styles.heroVideoCarousel}>
      <div className={styles.heroVideoTitle}>Sample Video</div>
      <div className={styles.heroVideoArea}>
        <video
          ref={videoRef}
          key={active}
          src={videos[active]}
          autoPlay
          muted={muted}
          playsInline
          onEnded={handleEnded}
          className={styles.heroVideo}
        />
        <button
          className={styles.heroVideoMute}
          onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <MuteIcon /> : <VolumeIcon />}
        </button>
        {videos.length > 1 && (
          <>
            <button
              className={`${styles.heroVideoNav} ${styles.heroVideoNavPrev}`}
              onClick={goPrev}
              aria-label="Previous video"
            >
              <ChevronIcon />
            </button>
            <button
              className={`${styles.heroVideoNav} ${styles.heroVideoNavNext}`}
              onClick={goNext}
              aria-label="Next video"
            >
              <ChevronIcon />
            </button>
          </>
        )}
        <div className={styles.heroVideoDots}>
          {videos.map((_, i) => (
            <button
              key={i}
              className={`${styles.heroVideoDot} ${i === active ? styles.active : ''}`}
              onClick={() => setActive(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const HeroImageCarousel: React.FC<{ images: string[] }> = ({ images }) => {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (images.length <= 1) return;
    const timer = setInterval(() => {
      setActive((prev) => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(timer);
  }, [images.length]);

  const goPrev = useCallback(() => {
    setActive((prev) => (prev - 1 + images.length) % images.length);
  }, [images.length]);

  const goNext = useCallback(() => {
    setActive((prev) => (prev + 1) % images.length);
  }, [images.length]);

  return (
    <div className={styles.heroImageCarousel}>
      <div className={styles.heroImageTrack}>
        {images.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`Sample image ${i + 1}`}
            className={`${styles.heroImageSlide} ${i === active ? styles.heroImageSlideActive : ''}`}
          />
        ))}
        {images.length > 1 && (
          <>
            <button
              className={`${styles.heroVideoNav} ${styles.heroVideoNavPrev}`}
              onClick={goPrev}
              aria-label="Previous image"
            >
              <ChevronIcon />
            </button>
            <button
              className={`${styles.heroVideoNav} ${styles.heroVideoNavNext}`}
              onClick={goNext}
              aria-label="Next image"
            >
              <ChevronIcon />
            </button>
          </>
        )}
        <div className={styles.heroVideoDots}>
          {images.map((_, i) => (
            <button
              key={i}
              className={`${styles.heroVideoDot} ${i === active ? styles.active : ''}`}
              onClick={() => setActive(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

const FAQItem: React.FC<{ question: string; answer: string }> = ({
  question,
  answer,
}) => {
  const [open, setOpen] = useState(false);
  const toggle = useCallback(() => setOpen((v) => !v), []);

  return (
    <div className={styles.faqItem}>
      <div className={styles.faqQuestion} onClick={toggle} role="button" tabIndex={0}>
        <h3>{question}</h3>
        <span className={`${styles.faqIcon} ${open ? styles.open : ''}`}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </div>
      <div className={`${styles.faqAnswer} ${open ? styles.open : ''}`}>
        <div
          className={styles.faqAnswerContent}
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      </div>
    </div>
  );
};

const CtaBanner: React.FC<{ config: ModelPageConfig }> = ({ config }) => {
  return (
    <section className={styles.ctaBanner}>
      <div className={styles.ctaBannerInner}>
        <div className={styles.ctaBannerBg}>
          <img
            src={
              config.cta.imageUrl ||
              'https://assets.falcocut.ai/models/sora/try-sora-2-pro-on-falcocut.png'
            }
            alt=""
          />
        </div>
        <div className={styles.ctaBannerContent}>
          <Link
            href="/ai-video-generator"
            className={`${styles.btnPrimary} ${styles.btnLarge}`}
          >
            {config.cta.buttonText}
          </Link>
        </div>
      </div>
    </section>
  );
};

/**
 * YouTube 嵌入网格 — 支持不可用自动降级
 *
 * 检测逻辑：通过 YouTube oEmbed 端点检查视频是否可访问。
 * 不可用时从 fallbacks 中取替代视频。
 */
type YouTubeItem = { videoId: string; url: string };

const YouTubeEmbedGrid: React.FC<{
  items: YouTubeItem[];
  fallbacks?: YouTubeItem[];
}> = ({ items: initialItems, fallbacks = [] }) => {
  const [activeItems, setActiveItems] = useState(initialItems);
  const fallbackPoolRef = useRef([...fallbacks]);

  useEffect(() => {
    let cancelled = false;
    const checkItems = async () => {
      const newItems = [...activeItems];
      let changed = false;

      for (let i = 0; i < newItems.length; i++) {
        if (cancelled) break;
        try {
          const res = await fetch(
            `https://www.youtube.com/oembed?url=https://www.youtube.com/watch?v=${newItems[i].videoId}&format=json`,
          );
          if (!res.ok && fallbackPoolRef.current.length > 0) {
            newItems[i] = fallbackPoolRef.current.shift()!;
            changed = true;
          }
        } catch {
          if (fallbackPoolRef.current.length > 0) {
            newItems[i] = fallbackPoolRef.current.shift()!;
            changed = true;
          }
        }
      }

      if (changed && !cancelled) {
        setActiveItems(newItems);
      }
    };

    checkItems();
    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.socialGrid}>
      {activeItems.map((item) => (
        <div key={item.videoId} className={styles.socialCardYoutube}>
          <iframe
            src={`https://www.youtube.com/embed/${item.videoId}?rel=0`}
            title=""
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ width: '100%', height: '100%', border: 'none' }}
          />
        </div>
      ))}
    </div>
  );
};

type RedditItem = {
  postUrl: string;
  title?: string;
  author?: string;
  subreddit?: string;
};

/**
 * Reddit 嵌入网格 — 支持不可用自动降级
 *
 * 检测逻辑：Reddit widgets.js 会将 blockquote.reddit-embed-bq 替换为 iframe。
 * 如果 8s 后 blockquote 仍存在（未被替换），说明该帖子不可用，
 * 从 fallbacks 中取下一个替换并重新加载 embed 脚本。
 */
const RedditEmbedGrid: React.FC<{
  items: RedditItem[];
  fallbacks?: RedditItem[];
}> = ({ items: initialItems, fallbacks = [] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeItems, setActiveItems] = useState(initialItems);
  const fallbackPoolRef = useRef([...fallbacks]);
  const hasCheckedRef = useRef(false);

  /** 加载 Reddit embed 脚本 */
  const loadRedditScript = useCallback(() => {
    document
      .querySelectorAll('script[src*="embed.reddit.com"]')
      .forEach((s) => s.remove());
    const script = document.createElement('script');
    script.src = 'https://embed.reddit.com/widgets.js';
    script.async = true;
    script.charset = 'utf-8';
    document.body.appendChild(script);
  }, []);

  useEffect(() => {
    loadRedditScript();
  }, [loadRedditScript]);

  /** 8s 后检查未成功加载的 embed，用 fallback 替换 */
  useEffect(() => {
    if (hasCheckedRef.current) return;
    const timer = setTimeout(() => {
      hasCheckedRef.current = true;
      if (!containerRef.current) return;
      const cards = containerRef.current.querySelectorAll(`.${styles.socialCardReddit}`);
      let needReload = false;
      const newItems = [...activeItems];

      cards.forEach((card, idx) => {
        const bq = card.querySelector('blockquote.reddit-embed-bq');
        if (bq && fallbackPoolRef.current.length > 0) {
          newItems[idx] = fallbackPoolRef.current.shift()!;
          needReload = true;
        }
      });

      if (needReload) {
        setActiveItems(newItems);
        setTimeout(() => loadRedditScript(), 100);
      }
    }, 8000);
    return () => clearTimeout(timer);
  }, [activeItems, loadRedditScript]);

  return (
    <div ref={containerRef} className={styles.socialGrid}>
      {activeItems.map((item, i) => (
        <div key={item.postUrl + i} className={styles.socialCardReddit}>
          <blockquote className="reddit-embed-bq" data-embed-theme="dark">
            <a href={item.postUrl}>{item.title || 'Reddit Post'}</a>
            <br />
            {item.author && (
              <>
                by <a href={`https://www.reddit.com/${item.author}/`}>{item.author}</a>
              </>
            )}
            {item.subreddit && (
              <>
                {' '}
                in{' '}
                <a href={`https://www.reddit.com/r/${item.subreddit}/`}>
                  {item.subreddit}
                </a>
              </>
            )}
          </blockquote>
        </div>
      ))}
    </div>
  );
};

/**
 * Twitter/X 嵌入网格 — 支持不可用自动降级
 *
 * 检测逻辑：widgets.js 会将 blockquote.twitter-tweet 替换为 iframe。
 * 如果 10s 后 blockquote 仍存在，说明该推文不可用，
 * 从 fallbacks 中取替代推文。
 */
const TwitterEmbedGrid: React.FC<{
  items: Array<{ tweetId: string }>;
  fallbacks?: Array<{ tweetId: string }>;
}> = ({ items: initialItems, fallbacks = [] }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [activeItems, setActiveItems] = useState(initialItems);
  const fallbackPoolRef = useRef([...fallbacks]);
  const hasCheckedRef = useRef(false);

  /** 加载 Twitter embed 脚本 */
  const loadTwitterScript = useCallback(() => {
    if ((window as any).twttr?.widgets) {
      (window as any).twttr.widgets.load(containerRef.current);
      return;
    }
    document
      .querySelectorAll('script[src*="platform.twitter.com"]')
      .forEach((s) => s.remove());
    const script = document.createElement('script');
    script.src = 'https://platform.twitter.com/widgets.js';
    script.async = true;
    script.charset = 'utf-8';
    document.body.appendChild(script);
    script.onload = () => {
      if ((window as any).twttr?.widgets) {
        (window as any).twttr.widgets.load(containerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    loadTwitterScript();
  }, [loadTwitterScript]);

  /** 10s 后检查未成功加载的 embed，用 fallback 替换 */
  useEffect(() => {
    if (hasCheckedRef.current) return;
    const timer = setTimeout(() => {
      hasCheckedRef.current = true;
      if (!containerRef.current) return;
      const cards = containerRef.current.querySelectorAll(`.${styles.socialCardTwitter}`);
      let needReload = false;
      const newItems = [...activeItems];

      cards.forEach((card, idx) => {
        const bq = card.querySelector('blockquote.twitter-tweet');
        if (bq && fallbackPoolRef.current.length > 0) {
          newItems[idx] = fallbackPoolRef.current.shift()!;
          needReload = true;
        }
      });

      if (needReload) {
        setActiveItems(newItems);
        setTimeout(() => loadTwitterScript(), 100);
      }
    }, 10000);
    return () => clearTimeout(timer);
  }, [activeItems, loadTwitterScript]);

  return (
    <div ref={containerRef} className={styles.socialGrid}>
      {activeItems.map((item) => (
        <div key={item.tweetId} className={styles.socialCardTwitter}>
          <blockquote className="twitter-tweet" data-theme="dark">
            <a href={`https://twitter.com/x/status/${item.tweetId}`}>Loading…</a>
          </blockquote>
        </div>
      ))}
    </div>
  );
};

/** Video Gallery — 8 个视频卡片网格，hover 展示完整 prompt */
const VideoGallery: React.FC<{
  gallery: NonNullable<ModelPageConfig['videoGallery']>;
}> = ({ gallery }) => {
  return (
    <section className={styles.videoGallery}>
      <h2 className={styles.sectionTitle}>{gallery.title}</h2>
      <p className={styles.sectionDesc}>{gallery.description}</p>
      <div className={styles.videoGalleryGrid}>
        {gallery.items.map((item) => (
          <VideoGalleryCard key={item.videoUrl} item={item} />
        ))}
      </div>
    </section>
  );
};

const VideoGalleryCard: React.FC<{
  item: NonNullable<ModelPageConfig['videoGallery']>['items'][number];
}> = ({ item }) => {
  const [muted, setMuted] = useState(true);
  const [promptHovered, setPromptHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggleMute = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setMuted((prev) => {
      if (videoRef.current) videoRef.current.muted = !prev;
      return !prev;
    });
  }, []);

  // prompt 截断显示前 120 字符
  const truncatedPrompt =
    item.prompt.length > 120 ? `"${item.prompt.slice(0, 120)}...` : `"${item.prompt}"`;

  return (
    <div className={styles.videoGalleryCard}>
      <h3 className={styles.videoGalleryCardTitle}>{item.title}</h3>
      <p className={styles.videoGalleryCardDesc}>{item.description}</p>
      <div className={styles.videoGalleryCardVideoWrap}>
        <video
          ref={videoRef}
          src={item.videoUrl}
          autoPlay
          muted={muted}
          loop
          playsInline
          className={styles.videoGalleryCardVideo}
        />
        <button
          className={styles.videoGalleryCardMute}
          onClick={toggleMute}
          aria-label={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? <MuteIcon /> : <VolumeIcon />}
        </button>
      </div>
      <div
        className={styles.videoGalleryCardPrompt}
        onMouseEnter={() => setPromptHovered(true)}
        onMouseLeave={() => setPromptHovered(false)}
      >
        <div className={styles.videoGalleryPromptLabel}>
          <span className={styles.videoGalleryPromptCaret}>❯_</span>
          {promptHovered ? ' FULL AI PROMPT' : ' PROMPT'}
        </div>
        <div className={styles.videoGalleryPromptPreview}>{truncatedPrompt}</div>
        {/* 悬浮展示完整 prompt，不占文档流 */}
        <div
          className={`${styles.videoGalleryPromptFull} ${promptHovered ? styles.visible : ''}`}
        >
          <div className={styles.videoGalleryPromptLabel}>
            <span className={styles.videoGalleryPromptCaret}>❯_</span> FULL AI PROMPT
          </div>
          <div className={styles.videoGalleryPromptFullText}>
            &ldquo;{item.prompt}&rdquo;
          </div>
        </div>
      </div>
    </div>
  );
};

const PlayIconAccent = () => (
  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
    <circle cx="32" cy="32" r="30" stroke="rgba(77,47,255,0.3)" strokeWidth="2" />
    <path d="M26 20L46 32L26 44V20Z" fill="rgba(77,47,255,0.4)" />
  </svg>
);

const CheckIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path
      d="M10 3L4.5 8.5L2 6"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const VideoIcon = () => (
  <svg width="24" height="16" viewBox="0 0 24 16" fill="none">
    <rect x="0.5" y="0.5" width="17" height="15" rx="2.5" stroke="currentColor" />
    <path d="M19 5L23 3V13L19 11V5Z" fill="currentColor" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 48 48">
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59a14.5 14.5 0 010-9.18l-7.98-6.19a24.08 24.08 0 000 21.56l7.98-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </svg>
);

export default ModelLandingPage;
